import React, { useState, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  IconButton
} from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import RestartAlt from '@mui/icons-material/RestartAlt';
import CheckCircle from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import Delete from '@mui/icons-material/Delete';
import { FACE_CONFIG } from '../config/faceRecognitionConfig';

const FaceRegistration = ({
  open,
  onClose,
  employeeId,
  token,
  onSuccess,
  isReregistration = false
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [capturedPhotos, setCapturedPhotos] = useState([]);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isTraining, setIsTraining] = useState(false);
  const [trainingProgress, setTrainingProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [hasPermission, setHasPermission] = useState(false);
  const [componentKey, setComponentKey] = useState(0); // 用於強制重新渲染，避免 DOM 錯誤

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const photosRef = useRef([]); // 用 ref 保存照片，避免狀態重置

  // 組件卸載時清理資源
  React.useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
      setCapturedPhotos([]);
      setError(null);
      setSuccess(false);
      setIsTraining(false);
      setHasPermission(false);
    };
  }, [cameraStream]);

  // 簡化版本：當 cameraStream 準備好時設置 video srcObject
  React.useEffect(() => {
    if (cameraStream && videoRef.current) {
      videoRef.current.srcObject = cameraStream;
    }
  }, [cameraStream]);

  const steps = [
    '準備相機',
    '拍攝照片',
    '訓練模型',
    '完成註冊'
  ];

  // 🎯 動態配置：從配置文件載入，可隨時調整無需修改代碼
  const requiredPhotos = FACE_CONFIG.REQUIRED_PHOTOS;
  const photoGuides = FACE_CONFIG.getPhotoGuides(requiredPhotos);

  // 初始化相機 - 簡化版本（與 CameraTest 一致）
  const initializeCamera = useCallback(async () => {
    try {
      setError(null);

      // 檢查瀏覽器支援
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('瀏覽器不支援相機');
      }

      // 先進入步驟 1，確保 video 元素已渲染
      setCurrentStep(1);

      // 等待 DOM 更新
      await new Promise(resolve => setTimeout(resolve, 500));

      // 使用配置文件中的相機設定
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          ...FACE_CONFIG.CAMERA_RESOLUTION,
          facingMode: 'user'
        },
        audio: false
      });

      setCameraStream(stream);
      setHasPermission(true);

      // 強制重新檢查狀態
      setTimeout(() => {
        setHasPermission(prev => {
          if (!prev) {
            return true;
          }
          return prev;
        });
      }, 100);

    } catch (err) {
      
      let errorMessage = '相機啟動失敗: ';
      if (err.name === 'NotAllowedError') {
        errorMessage += '相機權限被拒絕';
      } else if (err.name === 'NotFoundError') {
        errorMessage += '找不到相機設備';
      } else if (err.name === 'NotReadableError') {
        errorMessage += '相機被其他應用程式使用';
      } else {
        errorMessage += err.message;
      }
      
      setError(errorMessage);
      setHasPermission(false);
    }
  }, []);

  // 停止相機
  const stopCamera = useCallback(() => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
  }, [cameraStream]);

  // 拍攝照片 - 智能處理不同比例和高解析度
  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    setIsCapturing(true);
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    // 獲取原始影片尺寸
    const originalWidth = video.videoWidth;
    const originalHeight = video.videoHeight;
    const aspectRatio = originalWidth / originalHeight;

    // 固定使用 1280x720 高解析度
    let targetWidth = 1280;
    let targetHeight = 720;

    // 如果原始解析度不足，至少使用原始大小
    if (originalWidth < 1280 || originalHeight < 720) {
      targetWidth = Math.max(originalWidth, 1280);
      targetHeight = Math.max(originalHeight, 720);
    }

    // 設定 canvas 尺寸
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    // 因為 video 有鏡像翻轉，所以要在 canvas 上也進行翻轉以得到正確的圖像
    context.save();
    context.scale(-1, 1);
    
    // 繪製調整後的圖像
    context.drawImage(video, -targetWidth, 0, targetWidth, targetHeight);
    context.restore();

    // 轉換為 base64 - 使用配置文件中的品質設定
    const photoData = canvas.toDataURL('image/jpeg', FACE_CONFIG.JPEG_QUALITY);

    const newPhoto = {
      id: Date.now(),
      data: photoData,
      timestamp: new Date().toISOString(),
      resolution: `${targetWidth}x${targetHeight}`,
      originalResolution: `${originalWidth}x${originalHeight}`
    };

    setCapturedPhotos(prev => {
      const newPhotos = [...prev, newPhoto];
      // 同時更新 ref
      photosRef.current = newPhotos;
      return newPhotos;
    });

    setIsCapturing(false);

    // 如果拍攝完成，進入下一步
    const currentPhotoCount = capturedPhotos.length + 1;

    if (currentPhotoCount >= requiredPhotos) {
      setCurrentStep(2);
      stopCamera();
    }
  }, [capturedPhotos.length, requiredPhotos, stopCamera]);

  // 刪除照片
  const deletePhoto = useCallback((photoId) => {
    setCapturedPhotos(prev => prev.filter(photo => photo.id !== photoId));
  }, []);

  // 重新開始拍攝
  const retakePhotos = useCallback(() => {
    setCapturedPhotos([]);
    setCurrentStep(1);
    setError(null);
    initializeCamera();
  }, [initializeCamera]);

  // 訓練人臉模型
  const trainFaceModel = useCallback(async () => {
    try {
      setIsTraining(true);
      setTrainingProgress(0);
      setError(null);

      // 模擬進度更新 - 適應12分鐘訓練時間，避免卡在95%的錯覺
      const progressInterval = setInterval(() => {
        setTrainingProgress(prev => Math.min(prev + 1, 90)); // 緩慢增長，最大90%
      }, 6000); // 每6秒增加1%，約9分鐘到達90%，留出時間等待真實完成

      // 準備訓練資料 - 優先使用 ref 中的資料
      // 檢查兩個資料源
      const statePhotos = capturedPhotos;
      const refPhotos = photosRef.current;

      // 優先使用 ref 中的資料，因為它不會被重新渲染重置
      let photoData = [];

      if (refPhotos.length > 0) {
        photoData = refPhotos.map(photo => photo.data);
      } else if (statePhotos.length > 0) {
        photoData = statePhotos.map(photo => photo.data);
      } else {
        throw new Error('照片資料遺失，請重新拍攝照片');
      }
      
      const trainingData = {
        employeeId: employeeId,
        photos: photoData,
        required_photos: requiredPhotos  // 動態發送照片數量要求
      };

      // 檢查token是否存在
      if (!token) {
        throw new Error('登入令牌遺失，請重新登入');
      }

      // 呼叫後端 API - 設置12分鐘超時
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 720000); // 12分鐘超時

      const response = await fetch('/api/face/train-model', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(trainingData),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      // 檢查響應狀態
      if (!response.ok) {
        const errorText = await response.text();
        
        if (response.status === 401) {
          throw new Error('登入令牌已過期，請重新登入');
        } else if (response.status === 403) {
          throw new Error('權限不足，無法執行此操作');
        } else {
          throw new Error(`API 請求失敗 (${response.status}): ${errorText}`);
        }
      }

      // 安全地解析 JSON
      let result;
      try {
        const responseText = await response.text();
        result = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error('伺服器回應格式錯誤，請稍後再試');
      }

      clearInterval(progressInterval);
      setTrainingProgress(100);

      if (result.success) {
        setSuccess(true);
        setCurrentStep(3);

        // 通知父組件成功
        if (onSuccess) {
          onSuccess(result.data);
        }
      } else {
        throw new Error(result.error || '模型訓練失敗');
      }

    } catch (err) {
      
      // 處理不同類型的錯誤
      if (err.name === 'AbortError') {
        setError('訓練過程超時（超過12分鐘），請檢查網絡連接後重試');
      } else {
        setError(err.message || '訓練過程中發生錯誤，請重試');
      }
    } finally {
      setIsTraining(false);
    }
  }, [employeeId, capturedPhotos, token, onSuccess]);

  // 關閉對話框
  const handleClose = useCallback(() => {
    stopCamera();
    setCapturedPhotos([]);
    setCurrentStep(0);
    setError(null);
    setSuccess(false);
    setIsTraining(false);
    setTrainingProgress(0);
    setHasPermission(false);
    setComponentKey(prev => prev + 1); // 強制重新渲染，清理組件狀態
    onClose();
  }, [stopCamera, onClose]);

  // 渲染步驟內容
  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // 準備相機
        return (
          <Box textAlign="center" p={3}>
            <Typography variant="h6" gutterBottom>
              準備開始人臉註冊
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={3}>
              {isReregistration ? '重新註冊' : '首次註冊'}人臉識別需要拍攝 {requiredPhotos} 張不同角度的照片
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={3}>
              請確保：
              <br />• 光線充足
              <br />• 只有您一人在畫面中
              <br />• 允許瀏覽器使用相機權限
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<PhotoCamera />}
              onClick={initializeCamera}
              size="large"
            >
              開始註冊
            </Button>
          </Box>
        );

      case 1: // 拍攝照片 - 簡化版本（僅標題顯示指引）
        return (
          <Box textAlign="center">
            <Typography variant="h6" gutterBottom>
              拍攝照片 ({capturedPhotos.length}/{requiredPhotos}) - {photoGuides[capturedPhotos.length]?.title}（{photoGuides[capturedPhotos.length]?.description}）
            </Typography>
            
            {/* 簡化相機預覽 */}
            <Box mb={2}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{
                  width: '100%',
                  maxWidth: '500px',
                  maxHeight: '250px',
                  objectFit: 'cover',
                  border: '2px solid #4caf50',
                  borderRadius: '8px'
                }}
              />
              <canvas
                ref={canvasRef}
                style={{ display: 'none' }}
              />
            </Box>
            
            {/* 簡化狀態顯示 */}
            <Typography variant="body2" color={hasPermission ? 'success.main' : 'error.main'} mb={2}>
              狀態: {hasPermission ? '相機已就緒' : '等待相機'}
            </Typography>
            
            {/* 簡化拍照按鈕 */}
            <Button
              variant="contained"
              onClick={capturePhoto}
              disabled={isCapturing || !hasPermission}
              size="large"
            >
              {isCapturing ? '拍攝中...' : '拍照'}
            </Button>
            
            {/* 簡化照片預覽 */}
            {capturedPhotos.length > 0 && (
              <Box mt={3}>
                <Typography variant="body2" mb={2}>
                  已拍攝: {capturedPhotos.length}/{requiredPhotos}
                </Typography>
                <Box display="flex" gap={1} justifyContent="center" flexWrap="wrap">
                  {capturedPhotos.map((photo, index) => (
                    <img
                      key={photo.id}
                      src={photo.data}
                      alt={`照片 ${index + 1}`}
                      style={{
                        width: '60px',
                        height: '60px',
                        objectFit: 'cover',
                        border: '1px solid #ccc',
                        borderRadius: '4px'
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        );

      case 2: // 訓練模型
        return (
          <Box textAlign="center" p={3}>
            <Typography variant="h6" gutterBottom>
              {isTraining ? '正在訓練人臉模型...' : '準備訓練模型'}
            </Typography>
            
            {isTraining && (
              <>
                <LinearProgress 
                  variant="determinate" 
                  value={trainingProgress} 
                  sx={{ mb: 2 }}
                />
                <Typography variant="body2" color="textSecondary">
                  {trainingProgress}% 完成
                </Typography>
              </>
            )}

            {!isTraining && !success && (
              <>
                <Typography variant="body2" color="textSecondary" mb={3}>
                  已拍攝 {capturedPhotos.length} 張照片，點擊開始訓練人臉模型
                </Typography>
                
                <Box display="flex" gap={2} justifyContent="center">
                  <Button
                    variant="outlined"
                    onClick={retakePhotos}
                    startIcon={<RestartAlt />}
                  >
                    重新拍攝
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={trainFaceModel}
                  >
                    開始訓練
                  </Button>
                </Box>
              </>
            )}
          </Box>
        );

      case 3: // 完成註冊
        return (
          <Box textAlign="center" p={3}>
            <CheckCircle color="success" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              人臉註冊成功！
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={3}>
              您現在可以使用人臉識別進行簽到了
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isTraining}
      PaperProps={{
        sx: {
          mx: { xs: 1, sm: 2 }, // 手機上左右留更少邊距
          my: { xs: 1, sm: 2 }, // 手機上上下留更少邊距
          maxHeight: { xs: '95vh', sm: 'auto' } // 手機上限制最大高度
        }
      }}
    >
      <DialogTitle>
        {isReregistration ? '重新註冊人臉識別' : '註冊人臉識別'} 
        <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 1, fontSize: '0.8rem', fontWeight: 'bold' }}>
          {FACE_CONFIG.VERSION}
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        {/* 步驟指示器 */}
        <Stepper activeStep={currentStep} alternativeLabel sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* 錯誤訊息 */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} icon={<ErrorIcon />}>
            {error}
          </Alert>
        )}

        {/* 步驟內容 */}
        <Card>
          <CardContent>
            {renderStepContent()}
          </CardContent>
        </Card>
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={handleClose}
          disabled={isTraining}
        >
          {success ? '完成' : '取消'}
        </Button>
        
        {error && currentStep > 0 && (
          <Button
            onClick={() => {
              setError(null);
              setCurrentStep(0);
              setCapturedPhotos([]);
            }}
            color="primary"
          >
            重新開始
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FaceRegistration;